import { GradientHeading } from '@/components/GradientHeading'
import { Media } from '@/components/Media'
import { trackCta } from '@/lib/analytics'
import type { HeroContent, ThemeContent } from '@/lib/content.types'
import { fluidHeadingSize } from '@/lib/typography'

const GRID_MASK = 'radial-gradient(120% 88% at 50% 0%,#000 38%,transparent 76%)'

export function Hero({ data, theme }: { data: HeroContent; theme: ThemeContent }) {
  return (
    // 글로우/격자가 섹션 밖으로 새지 않도록 여기서 클리핑한다
    // (페이지 래퍼에 걸면 sticky nav가 깨진다)
    <header
      id="top"
      // 위 여백은 큰 제목 한 줄 높이의 **절반**만큼 줄여 콘텐츠 전체를 끌어올렸다
      // (데스크톱 제목 52px → 줄높이 49px → 24px, 모바일 26px → 24px → 12px)
      className="relative overflow-hidden px-5 pb-16 pt-[60px] md:px-10 md:pb-24 md:pt-[94px]"
    >
      {/* 글로우 */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-200px] h-[640px] w-[940px] -translate-x-1/2 rounded-full blur-[34px]"
        style={{
          opacity: theme.heroGlow ? 0.7 : 0,
          background:
            'radial-gradient(52% 54% at 44% 42%, rgba(46,144,250,.40), transparent 62%), radial-gradient(46% 48% at 62% 60%, rgba(95,18,204,.30), transparent 64%)',
        }}
      />
      {/* 격자 */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          opacity: theme.showGrid ? 1 : 0,
          backgroundImage:
            'linear-gradient(#ffffff08 1px,transparent 1px),linear-gradient(90deg,#ffffff08 1px,transparent 1px)',
          backgroundSize: '58px 58px',
          maskImage: GRID_MASK,
          WebkitMaskImage: GRID_MASK,
        }}
      />

      <div className="relative mx-auto max-w-[1000px] text-center">
        <div className="mb-[26px] animate-fade font-mono text-[12.5px] font-medium uppercase tracking-[.22em] text-cyan-brand">
          {data.eyebrow}
        </div>

        <h1
          className="mb-[26px] animate-rise font-headline font-bold leading-headline tracking-headline [animation-delay:.05s]"
          // Admin에서 정한 크기를 데스크톱 상한으로 쓰고, 좁은 화면에서는 자동으로 줄인다.
          // 하한을 상한의 절반으로 두면 모바일 값을 따로 관리하지 않아도 된다.
          style={{ fontSize: fluidHeadingSize(data.headingSize) }}
        >
          {/* 제목 전체를 쓰고, 그 안에서 강조할 부분만 그라디언트로 칠한다 */}
          <GradientHeading heading={data.heading} highlight={data.highlight} />
        </h1>

        <p className="mx-auto mb-9 max-w-[600px] animate-rise text-[17px] leading-[1.6] text-ink-secondary [animation-delay:.12s] md:text-lg">
          {data.subcopy}
        </p>

        <div className="flex animate-rise flex-col items-center justify-center gap-3.5 [animation-delay:.18s] sm:flex-row">
          <a
            href={data.primaryCta.href}
            onClick={() => trackCta('hero_primary', data.primaryCta.label, data.primaryCta.href)}
            className="annx-cta w-full rounded-[10px] px-[26px] text-[15px] text-white shadow-cta-hero transition-[filter] hover:brightness-110 sm:w-auto"
            style={{ background: 'var(--accent)' }}
          >
            {data.primaryCta.label}
          </a>
          <a
            href={data.secondaryCta.href}
            onClick={() =>
              trackCta('hero_secondary', data.secondaryCta.label, data.secondaryCta.href)
            }
            className="annx-cta w-full rounded-[10px] border border-line-button px-6 text-[15px] text-ink-primary transition-colors hover:border-blue-brand sm:w-auto"
          >
            {data.secondaryCta.label}
          </a>
        </div>
      </div>

      {/**
       * 대표 영상 — 버튼과의 간격을 위쪽 "설명 문구 ↔ 버튼" 간격과 **눈에 보이는 기준**으로 맞췄다.
       *
       * 위쪽은 박스 간격이 36px(mb-9)이지만, 설명 문구의 줄간격(1.6) 때문에 마지막 글자 아래로
       * 약 5px의 빈 공간이 더 있어 실제로는 41px처럼 보인다. 반면 영상은 테두리가 곧 시작점이라
       * 박스 간격이 그대로 보인다. 그래서 36px로 맞추면 영상 쪽이 더 붙어 보인다.
       * 실제 렌더 픽셀로 측정해 정했다 — 36으로 되돌리지 말 것.
       * 모바일은 설명 문구가 17px이라 빈 공간이 조금 더 커서 44px, 데스크톱은 41px.
       */}
      <div className="relative mx-auto mt-[44px] max-w-hero animate-rise-slow [animation-delay:.28s] md:mt-[41px]">
        <div className="relative aspect-[16/8] overflow-hidden rounded-hero border border-line-media shadow-hero-media">
          <Media media={data.media} loading="eager" />
        </div>
      </div>
    </header>
  )
}
